import { Button, TYPE } from '@common/Button/Button';
import { Checkbox } from '@common/Checkbox/Checkbox';
import { Icon, IconColor, IconID } from '@common/Icon/Icon';
import type { CfCaseProductBoxProps } from '@libs/cfCase.helper';
import { getLocale } from '@libs/constants';
import { EXTERNAL_LINKS } from '@libs/redirect.helper';
import { ProductBox } from '@product/ProductBox/ProductBox';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useState } from 'react';
import styles from '../Default/BuyBox.module.scss';

interface CFCaseSelectorModalContentProps {
  cfCases?: CfCaseProductBoxProps[];
  onAddCFCaseConfigurationToShoppingCart: (sku: string | null | undefined) => void;
}

export const CFCaseSelectorModalContent = ({
  cfCases,
  onAddCFCaseConfigurationToShoppingCart,
}: CFCaseSelectorModalContentProps) => {
  const { t } = useTranslation('product/cfCaseSelectorModalContent');
  const [selectedCFcaseSku, setSelectedCFcaseSku] = useState<string | null | undefined>();
  const router = useRouter();
  const locale = getLocale(router);
  const cfCasesStandard = cfCases.filter(cfCase => cfCase.design === 'val_1');
  const cfCasesRefill = cfCases.filter(cfCase => cfCase.design === 'val_123');

  const cfCasesStandardList = !cfCasesStandard?.length
    ? null
    : cfCasesStandard.map(cfcase => (
        <div className="flex items-center igus-text-styles pb-8 mb-4 border-b" key={`cfcaseOption_${cfcase.sku}`}>
          <Checkbox
            id={`cfcaseOption_${cfcase.sku}`}
            data-testid={`checkbox_cfcaseOption_${cfcase.sku}`}
            onChange={value => setSelectedCFcaseSku(value ? cfcase.sku : null)}
            value={selectedCFcaseSku === cfcase.sku}
          />
          <ProductBox
            sku={cfcase.sku}
            title={cfcase.title}
            subtitle={cfcase.subTitle}
            image={cfcase.image}
            pricePerUnit={cfcase.pricePerUnit}
            currencyCode={cfcase.currencyCode}
            linkTargetOverride="/info/chainflex-case"
            externalLink
            showPrice
            option
            recommended={cfCases.length === 1 || cfcase.caseCode === 'S'}
          />
        </div>
      ));

  const cfCasesRefillList = !cfCasesRefill?.length ? null : (
    <>
      <p
        className="mt-5 mb-4 text-sm text-gray-400"
        dangerouslySetInnerHTML={{
          __html: t('CF_CASE_SELECTOR.CF_CASE_REFILL_INTRO', {
            link: EXTERNAL_LINKS.CF_CASE_REFILL_INFO(locale),
          }),
        }}
      />
      {cfCasesRefill.map(cfcase => (
        <div className="flex items-center igus-text-styles pb-8 mb-4 border-b" key={`cfcaseOption_${cfcase.sku}`}>
          <Checkbox
            id={`cfcaseOption_${cfcase.sku}`}
            data-testid={`checkbox_cfcaseOption_${cfcase.sku}`}
            onChange={value => setSelectedCFcaseSku(value ? cfcase.sku : null)}
            value={selectedCFcaseSku === cfcase.sku}
          />
          <ProductBox
            sku={cfcase.sku}
            title={cfcase.title}
            subtitle={cfcase.subTitle}
            image={cfcase.image}
            pricePerUnit={cfcase.pricePerUnit}
            currencyCode={cfcase.currencyCode}
            recommended={cfCases.length === 1 || cfcase.caseCode === 'S'}
            linkTargetOverride="/info/chainflex-case"
            externalLink
            showPrice
            option
          />
        </div>
      ))}
    </>
  );

  return (
    <div className={classNames(styles.CFCaseSelectorModalContent)}>
      <p
        className="mb4 pb-8 igus-text-styles border-b"
        dangerouslySetInnerHTML={{
          __html: t('CF_CASE_SELECTOR.ABOUT'),
        }}
      />
      <p
        className="mt-5 mb-4 text-sm text-gray-400"
        dangerouslySetInnerHTML={{
          __html: t('CF_CASE_SELECTOR.DETAILS', {
            link: EXTERNAL_LINKS.CF_CASE_INFO(locale),
          }),
        }}
      />
      {cfCasesStandardList}
      {cfCasesRefillList}
      <div className="flex items-center igus-text-styles pb-8 mb-4 align-middle" key="cfcaseOption_none">
        <Checkbox
          id="cfcaseOption_none"
          onChange={() => setSelectedCFcaseSku(null)}
          value={selectedCFcaseSku === null}
        />
        <span className={classNames('pt-4')}>{t('CF_CASE_SELECTOR.WITHOUT_CASE_OPTION')}</span>
      </div>
      <div className="flex justify-end">
        <Button
          icon={<Icon size={10} color={IconColor.WHITE} id={IconID.CART} />}
          text={t('CF_CASE_SELECTOR.ADD_TO_CART')}
          type={TYPE.PRIMARY}
          disabled={selectedCFcaseSku === undefined}
          onClick={() => onAddCFCaseConfigurationToShoppingCart(selectedCFcaseSku)}
        />
      </div>
    </div>
  );
};
